import type { ErrorOptions, ErrorWithRavenOptions } from './errors/Error';
import type { AjaxErrorInfo, AjaxRequestInfo, FetchErrorInfo } from '@easterngraphics/wcf/modules/utils/async';

import merge from 'lodash/merge';

import { triggerSessionExpired } from '../app-api/AppAction';

import { developmentMode } from './ReactScriptHelper';
import { notNull } from './Types';

import { SoapError } from '@easterngraphics/wcf/modules/eaiws';
import { wcfConfig } from '@easterngraphics/wcf/modules/utils';
import { CustomError } from '@easterngraphics/wcf/modules/utils/error';

const networkErrorQueue: Array<AjaxErrorInfo | FetchErrorInfo> = [];

export function retryNetworkRequests(): void {
    networkErrorQueue.forEach((error: AjaxErrorInfo | FetchErrorInfo): void => {
        error.retry();
    });
    networkErrorQueue.length = 0;
}

export function getQueuedNetworkErrorDetails(): Array<AjaxRequestInfo> {
    return networkErrorQueue.map((error: AjaxErrorInfo | FetchErrorInfo): AjaxRequestInfo | null => {
        if ((error as AjaxErrorInfo).requestInfo != null) {
            return (error as AjaxErrorInfo).requestInfo;
        }
        return null;
    }).filter(notNull);
}

export type AjaxSoapErrorInfo = AjaxErrorInfo & { responseError: SoapError };
function isAjaxSoapErrorInfo(error: AjaxErrorInfo): error is AjaxSoapErrorInfo {
    return error.responseError instanceof SoapError;
}

export type FetchSoapErrorInfo = FetchErrorInfo & { error: SoapError };
function isFetchSoapErrorInfo(error: FetchErrorInfo): error is FetchSoapErrorInfo {
    return error.error instanceof SoapError;
}

interface AjaxErrorHandlerCallbacks {
    networkError: VoidFunction;
    soapError: (error: AjaxSoapErrorInfo | FetchSoapErrorInfo) => void;
    eaiwsSessionExpired: (error: AjaxSoapErrorInfo | FetchSoapErrorInfo) => void;
}

export class IgnoreAjaxErrorHandlerError extends CustomError {
    constructor(message?: string, public xhr?: XMLHttpRequest) {
        super(message);

        Object.setPrototypeOf(this, IgnoreAjaxErrorHandlerError.prototype);
    }
}

export function ignoreAjaxErrorHandlerMessageCallback(error: Error | string, xhr: XMLHttpRequest, response: unknown): Error {
    const message: string = error instanceof Error ? error.message : error.toString();
    return new IgnoreAjaxErrorHandlerError(message, xhr);
}

export function registerAjaxErrorHandler(callbacks: AjaxErrorHandlerCallbacks): void {
    if (wcfConfig.ajaxErrorHandler != null) {
        if (developmentMode) {
            console.warn('An global ajax error handler has already been registered');
        } else {
            throw new Error('An global ajax error handler has already been registered');
        }
    }

    wcfConfig.fetchErrorHandler = (error: FetchErrorInfo): void => {
        if (error.error instanceof IgnoreAjaxErrorHandlerError) {
            error.reject();
        } else if (isFetchSoapErrorInfo(error)) {
            // Note: double checking of `isinstance` vs another function argument (ts type guard)
            if (error.error.message.includes('unknown session')) {
                triggerSessionExpired();
                callbacks.eaiwsSessionExpired(error);
            } else {
                // Note: add information about the soap request
                (error.error as ErrorWithRavenOptions).ravenOptions = merge<
                    ErrorOptions,
                    ErrorOptions | undefined
                >(
                    {
                        extra: {
                            soapRequest: 'fetch'
                        }
                    },
                    (error.error as ErrorWithRavenOptions).ravenOptions
                );

                callbacks.soapError(error);
            }
        } else if (error.isNetworkError || error.isTimeoutError) { // Network error
            if (error.url.startsWith('http:') && document.location.protocol === 'https:') {
                // mixed content
                error.reject();
            } else {
                networkErrorQueue.push(error);
                callbacks.networkError();
            }
        } else {
            error.reject();
        }
    };

    // connect global ajax error handler
    wcfConfig.ajaxErrorHandler = (error: AjaxErrorInfo): void => {
        if (error.responseError instanceof IgnoreAjaxErrorHandlerError) {
            error.reject();
        } else if (isAjaxSoapErrorInfo(error)) {
            // Note: double checking of `isinstance` vs another function argument (ts type guard)
            if (error.responseError.message.includes('unknown session')) {
                triggerSessionExpired();
                callbacks.eaiwsSessionExpired(error);
            } else {
                // Note: add information about the soap request
                (error.responseError as ErrorWithRavenOptions).ravenOptions = merge<
                    ErrorOptions,
                    ErrorOptions | undefined
                >(
                    {
                        extra: {
                            soapRequest: error.requestInfo.data
                        }
                    },
                    (error.responseError as ErrorWithRavenOptions).ravenOptions
                );

                callbacks.soapError(error);
            }
        } else if (error.status === 0) { // Network error
            if (error.requestInfo.url.startsWith('http:') && document.location.protocol === 'https:') {
                // mixed content
                error.reject();
            } else {
                networkErrorQueue.push(error);
                callbacks.networkError();
            }
        } else {
            error.reject();
        }
    };
}